import { computed, reactive, ref, unref } from 'vue'
import formUtils from '@/utils/form'
import { noEmptyRules } from '@/validation'
import { mapKeys } from 'lodash'
import { GetTags } from '@/api/memberTags'
import store from '@/store'
import { mTagAssistantErrors } from '@/config/mTagAssistant'

const createDefaultState = () => ({
  name: '',
  mTagGroupIds: [],
  isAuto: false,
})

const cloneFieldValue = (value) => {
  if (Array.isArray(value)) return [...value]
  return value
}

export const useTagForm = (sourceData) => {
  const form = ref(null)

  const formData = reactive(createDefaultState())

  const defaultState = createDefaultState()

  const formRules = {
    name: [
      noEmptyRules('請輸入標籤名稱'),
      { max: 20, message: '標籤名稱最多 20 個字', trigger: ['change', 'blur'] },
      {
        trigger: ['change', 'blur'],
        validator: (rule, value, callback) => {
          if (/\s/g.test(value)) { callback(new Error('標籤名稱不可包含空白')) }
          callback()
        },
      },
    ],
  }

  // 前端預檢同名標籤衝突，最終仍以後端 400 為準
  // intent='rule' 不可對到既有手動標籤；intent='import' 不可對到既有自動標籤
  const checkNameConflict = async (intent) => {
    const queryName = formData.name
    if (!queryName) return null
    const shopId = store.getters.shop
    const [data, err] = await GetTags({ shopId, name: queryName, limit: 5 })
    // 期間使用者已改名稱，這次結果作廢，避免拿舊查詢去判斷新名稱
    if (formData.name !== queryName) return null
    if (err) return null
    const found = (data || []).find((t) => t.name === queryName)
    if (!found) return null
    // 編輯模式下找到的是自己不算衝突
    const currentId = unref(sourceData)?.id
    if (currentId && found.id === currentId) return null

    if (intent === 'rule' && !found.isAuto) {
      return mTagAssistantErrors.manualTagCannotSetRule(queryName)
    }
    if (intent === 'import' && found.isAuto) {
      return mTagAssistantErrors.autoTagCannotImport(queryName)
    }
    if (intent === 'import' && !found.isAuto) {
      return mTagAssistantErrors.manualTagAlreadyExists(queryName)
    }
    return null
  }

  const resetForm = () => {
    mapKeys(defaultState, (v, k) => {
      formData[k] = cloneFieldValue(v)
    })
    if (form.value) {
      formUtils.resetForm(form.value)
    }
  }

  const syncForm = () => {
    mapKeys(formData, (_, k) => {
      const v = unref(sourceData)[k]
      formData[k] = v !== undefined ? cloneFieldValue(v) : cloneFieldValue(defaultState[k])
    })
  }

  const submitForm = async () => {
    const valid = await formUtils.checkForm(form.value)
    if (!valid) return false
    return true
  }

  const isDataChanged = computed(() => {
    let isChanged = false
    mapKeys(formData, (v, k) => {
      const sourceValue = unref(sourceData)[k]
      if (Array.isArray(v) || Array.isArray(sourceValue)) {
        const left = Array.isArray(v) ? v : []
        const right = Array.isArray(sourceValue) ? sourceValue : []
        if (left.length === right.length && left.every((item, index) => item === right[index])) return
        isChanged = true
        return
      }
      if (sourceValue === v) return
      isChanged = true
    })
    return isChanged
  })

  return {
    form,
    formData,
    resetForm,
    syncForm,
    submitForm,
    formRules,
    isDataChanged,
    checkNameConflict,
  }
}
